import { observable } from 'mobx';
import { AGGREGATORS } from '../constants/aggregators';
import { ITakerGoInfo } from '../view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/types/deliveryTracking';
import { TBooleanInNumber, TCRUDEvents, TTranslate } from './main';
import { PRODUCT_TYPE } from './product';
import { ICustomer } from './user';
import { ORDER_NOTIFICATION_TYPE, ORDER_STATUS } from 'src/constants/order';
import { MEASURE, PAYMENT_TYPES } from 'src/constants/main';
import { ORDER_DELIVERY_TYPES } from 'src/constants/deliveryTypes';

interface IDiscountError {
  error_promo: string;
  active_promocode: boolean;
  error_type: string;
  errorCode: number;
  promocode_minimum: number;
}

export enum typeSMS {
  WITH_GEOLOCATION = 1,
  WITHOUT_GEOLOCATION = 2,
}

export interface ICartTotal {
  total: number;
  subtotal: number;
  earn_points: number;
  redeem_points: number;
  discount_amount: number;
  discounted_total: number;
  discounted_subtotal: number;
  left_to_free_delivery: number;
  left_to_min_order: number;
  vat: number;
  loading: boolean;
  discount_error: undefined | IDiscountError;
  is_free_delivery: number;
  delivery_type: number;
  taxable_delivery_fee: number;
  min_order: number;
  free_delivery: number;
  max_cash_payment: number;
  delivery_fee: number;
  promocode: string;
  wallet_discount: number;
}

export type TOrder = {
  id: string;
  branch: {
    id: number;
    name: TTranslate;
    lat: number;
    lng: number;
    address: string;
    mobile: string;
  } | null;
  address: string; // delivery address or branch address based on delivery type    "hash": String,
  order_key: number;
  invoice_number: number;
  /**
   * @deprecated since Franchise 1.2
   *
   * @type {string}
   * @memberof IOrder
   */
  currency_code: string;
  /**
   * @deprecated since Franchise 1.2
   *
   * @type {string}
   * @memberof IOrder
   */
  currency_position?: string;
  currency: {
    currency_code: string;
    currency_position: string;
    divided: number;
  } | null;
  customer_first_name: string;
  customer_mobile: string;
  customer_email: string;
  customer: ICustomer;
  stranger_customer_info: {
    name: string;
    mobile: string;
    stranger_id: ICustomer['id'];
  };
  is_order_for_someone_else: boolean;
  total: number;
  subtotal: number;
  sms?: {
    resend_available_at: number;
    type: typeSMS;
  };
  donation_subtotal?: number;
  service_tax_percentage: number;
  service_tax: number;
  vat_percentage: number;
  vat: number;
  cashback_amount: number;
  promocode: string;
  promocode_amount: number;
  promocode_value: number;
  promocode_type: number | string;
  promocode_description?: string;
  delivery_type: string;
  delivery_fee: number;
  taxable_delivery_fee: number;
  is_curbside?: number;
  payment_status: string;
  payment_type: PAYMENT_TYPES;
  status: string;
  latitude: number;
  longitude: number;
  aggregator_key: AGGREGATORS | undefined | null;
  aggregator_id: string | undefined | null;
  notes: string;
  order_datetime: string;
  customer_id: number;
  address_id: number;
  address_type_id: number;
  is_verified: number;
  is_address_change: number;
  accepted_at: string;
  confirmed_at: null;
  assigned_at: null;
  delivered_at: null;
  created_by: string;
  created_by_code: string;
  delivery_time_name: number;
  earn_point: number;
  created_by_name: string;
  deliver_at: null;
  redeem_point: number;
  area_id: number;
  kiosk_id: number;
  tax: number;
  include_delivery_vat: number;
  include_vat: number;
  vat_delivery: number;
  tax_delivery: number;
  is_restored_payment_id: number;
  is_manual_refund: number;
  is_refund: number;
  refund?: number;
  created_at: string;
  customer_name: string;
  curbside?: null | {
    driver_name: string;
    car_brand: string;
    car_color: string;
    car_type: string;
    car_number: string;
    curbside_fee: number;
    client_arrived: number;
    client_arrived_at: null | string;
  };
  edited?: boolean;
  viewed: number;
  send_datetime: string;
  notification_type: ORDER_NOTIFICATION_TYPE;
  deleted_at: null;
  delivery_type_code: ORDER_DELIVERY_TYPES;
  order_status_code: ORDER_STATUS;
  previous_status: ORDER_STATUS;
  vat_tax_percentage: number;
  order_date: string;
  takergo?: null | ITakerGoInfo;
  products?: IOrderProduct[];
  dook_id?: string;
  payment_id?: string;
  foodics_id?: string;
  foodics_reference?: string;
  pos_system_name?: string;
  use_wallet?: number;
  wallet_discount?: number;
  discounted_total?: number;
  ahead: number;
  stranger_id?: null | number;
  stranger_name?: null | string;
  stranger_phone?: null | string;
  dinein?: {
    dinein_delivery_type: number; // 1 - OT_DINEIN, 2 - OT_TOGO
    section?: {
      id: number;
      name: TTranslate;
    };
    table?: {
      id: number;
      name: TTranslate;
    };
  };
  updated_at?: string;
  draft_created_by?: number | null;
  draft_created_at?: string | null;
  last_updated_by?: number | null;
  last_updated_at?: string | null;
  last_updated_username?: string | null;
  dnf: {
    enable: boolean;
    cutoff_at: string;
  };

  aggregator: {
    tenant_id: number;
    external_transaction_id: string;
    dook_tenant_type: number;
  };
  rating?: {
    id: number;
    avg_rating: number;
    meal: number;
    service: number;
    overall: number;
    comment: string | null;
    order_id: number;
    customer_id: number;
    created_at: string;
    updated_at: string;
  };
  is_zone_address: boolean;
  zone_name: TTranslate;
};

export type TOrderStatuses = {
  WAITING_FOR_PAYMENT: 'w';
  PENDING: 'p';
  ACCEPTED: 'a';
  PROCESSING: 'pr';
  PICKEDUP: 'pi';
  OUT_FOR_DELIVERY: 'o';
  ASSIGNED: 'as';
  WAITING_FOR_PICKUP: 'da';
  COMPLETED: 'd';
  RETURNED: 'r';
  CANCELED: 'ca';
};

export type TOrdersFilters = {
  ALL: 'all';
  ALL_ACTIVE: 'all_active';
  NEW_ACTIVE: 'new_active';
  IN_PROGRESS_ACTIVE: 'in_progress_active';
  IN_DELIVERY_ACTIVE: 'in_delivery_active';
  RETURNED_ACTIVE: 'returned_active';
  ALL_AHEAD: 'all_ahead';
  NEW_AHEAD: 'new_ahead';
  IN_PROGRESS_AHEAD: 'in_progress_ahead';
  ALL_HISTORY: 'all_history';
  COMPLETED_HISTORY: 'completed_history';
  CANCELED_HISTORY: 'canceled_history';
};

export interface IOrderProduct {
  id: number;
  name: TTranslate;
  price: number;
  description: TTranslate;
  image: string;
  // order_id: number;
  quantity: number;
  notes: string;
  modifiers: IOrderProductModifier[];
  exclusions: IOrderProductExclusion[];
  size?: IOrderProductSize | null;
  is_donation?: TBooleanInNumber;
  is_delete?: TBooleanInNumber;
  status?: TBooleanInNumber;
  out_of_stock?: TBooleanInNumber;
  type?: PRODUCT_TYPE;
  items?: IOrderProductComboItem[];
}

export interface IOrderProductComboItem {
  id: number;
  name: string;
  description: string;
  price: string; // float number
  image: string;
  modifiers: IOrderProductModifier[];
  exclusions: IOrderProductExclusion[];
  category_status?: TBooleanInNumber;
  category_is_delete?: TBooleanInNumber;
  is_donation?: TBooleanInNumber;
  is_size?: TBooleanInNumber;
  is_modifiers?: TBooleanInNumber;
  is_required_modifiers?: TBooleanInNumber;
  is_exclusions?: TBooleanInNumber;
  status?: TBooleanInNumber;
  is_delete?: TBooleanInNumber;
  out_of_stock?: TBooleanInNumber;
  // meal keys
  size_id?: number;
  group_id?: number;
  choice_id?: number;
  selected?: boolean;
}

export interface IOrderProductModifierOption {
  id: number;
  name: TTranslate;
  price: number;
  weight: number;
  unit: MEASURE;
  quantity: number;
  is_delete?: TBooleanInNumber;
  status?: TBooleanInNumber;
}

export interface IOrderProductModifier {
  id: number;
  name: TTranslate;
  price: number;
  options?: IOrderProductModifierOption[];
  is_delete?: TBooleanInNumber;
  status?: TBooleanInNumber;
}

export interface IOrderProductExclusion {
  id: number;
  name: TTranslate;
  status?: TBooleanInNumber;
}

export interface IOrderProductSize {
  id: number;
  name: TTranslate;
  price: number;
  weight: number;
  unit: MEASURE;
  is_delete?: TBooleanInNumber;
  status?: TBooleanInNumber;
}

export type TOrderTechnicalLog = {
  id: number;
  order_id: number;
  status: string;
  time: number;
  json: any;
};

export enum TRANSACTION_STATUSES {
  UNKNOWN = 0,
  SENT = 1,
  DONE = 2,
  FAILED = 3,
}

export type TOrderTransactionLog = {
  id: number;
  order_id: number;
  user_id: number;
  username: string | null;
  transaction_id: string;
  status: TRANSACTION_STATUSES;
  transaction_response: string | null;
  action: number;
  payment_type: number;
  amount: number;
  currency_code?: string;
  reason: string;
  created_at: string;
  updated_at: string;
  action_name: string;
  arn: string | null;
};

export type TOrderContentLog = {
  id: number;
  order_id: number;
  status: string;
  time: number;
  json: any;
};

export type TOrderLog = {
  id: number;
  order_id: number;
  status: string;
  time: number;
  json: {
    id: number;
    time: number;
    old_status: string;
    new_status: string;
    author: string;
  };
  notes?: string | null;
};

export type TOrderCRUDEvents = TCRUDEvents | 'driver_not_found' | 'client_arrived' | 'ahead';

export type TEventMessage<T, B> = { id: string; source: string; event: B; data: T };

const getOrderAhead = (data: any): number => {
  if ([AGGREGATORS.CAREEM, AGGREGATORS.JAHEEZ, AGGREGATORS.HUNGER_STATION, AGGREGATORS.TO_YOU, AGGREGATORS.KEETA].includes(data.aggregator_key))
    return 1;

  /** Return real ahead value in order comes not from aggregator */
  return Number(data.ahead);
};

export const orderCast = (data: any): TOrder => ({
  ...data,
  id: String(data.id),
  ahead: getOrderAhead(data),
  viewed: Boolean(data.viewed),
});

export const ordersCast = (data: any[]) => {
  const orders = data.map(orderCast);
  return observable.map(orders.map((order) => [order.id, order]));
};
